<template>
  <AppShellSkeleton v-if="authenticated" />
  <div v-else style="width: 100%">
    <!-- Check Your Inbox confirmation (shown after forgot-password submit) -->
    <v-card
      v-if="showConfirmation"
      class="mx-auto pa-6"
      max-width="460"
      rounded="lg"
      elevation="6"
    >
      <div class="text-center mb-4">
        <v-icon size="48" color="primary">mdi-email-outline</v-icon>
      </div>
      <h2 class="text-h6 text-center mb-3">Check Your Inbox</h2>
      <ol class="body-2 grey--text text--darken-1 pl-4 mb-4">
        <li class="mb-1">Open your email or SMS</li>
        <li class="mb-1">Click <strong>Reset My Password</strong></li>
        <li class="mb-1">Set a new password</li>
        <li>Sign in with your new password</li>
      </ol>
      <v-alert type="info" text dense class="caption mb-4">
        Can't find the link? Check your spam folder. If your contact method has
        changed, ask your Manager to update it in the platform.
      </v-alert>
      <v-btn
        text
        block
        @click="
          showConfirmation = false;
          remindPassword = false;
        "
      >
        <v-icon left>mdi-arrow-left</v-icon>
        Back to Sign In
      </v-btn>
    </v-card>

    <CardLayoutAuthenticationForm
      v-else-if="remindPassword"
      title="Forgot Password"
      submit-text="Send Reset Link"
      :in-process="inProcess"
      @cancel="remindPassword = false"
      @submit="tryRemind"
    >
      <ValidatedTextField
        v-model="username"
        :disabled="inProcess"
        name="Username"
        rules="required"
        outlined
        dense
      />
    </CardLayoutAuthenticationForm>

    <CardLayoutAuthenticationForm
      v-else
      title="Log In"
      submit-text="Log In"
      :in-process="inProcess"
      :show-cancel="false"
      @submit="tryLogin"
    >
      <v-alert v-if="sessionWasExpired" type="info" text dense class="mb-4">
        Your session expired. Please sign in again.
      </v-alert>
      <ValidatedTextField
        v-model="username"
        :disabled="inProcess"
        :autofocus="true"
        name="Username"
        rules="required"
        outlined
        dense
      />
      <ValidatedTextField
        v-model="password"
        :disabled="inProcess"
        :type="showPassword ? 'text' : 'password'"
        :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
        name="Password"
        rules="required|min:6|max:128"
        outlined
        dense
        @click:append="togglePasswordVisibility"
      />
      <p class="pa-0 ma-0">
        <a @click="remindPassword = true"> Forgot Password? </a>
      </p>
    </CardLayoutAuthenticationForm>
  </div>
</template>

<script>
import CardLayoutAuthenticationForm from "./CardLayoutAuthenticationForm.vue";
import AppShellSkeleton from "@layout/AppShellSkeleton.vue";
import ValidatedTextField from "../utils/ValidatedTextField.vue";
import useRecaptcha from "@tod-ui/composables/useRecaptcha.js";
import { ref, watch, onMounted } from "vue";
import { required, max, min } from "vee-validate/dist/rules";
import { extend } from "vee-validate";

import useUser from "./useUser";
import { sessionExpired } from "../../router/auth-hooks";
import useAlerts from "@tod-ui/composables/useAlerts";

extend("max", {
  ...max,
  message: "{_field_} may not be longer than {length} characters",
});

extend("min", {
  ...min,
  message: "{_field_} must be at least {length} characters",
});

extend("required", {
  ...required,
  message: "{_field_} can not be empty",
});

export default {
  name: "PageLogin",
  components: {
    CardLayoutAuthenticationForm,
    AppShellSkeleton,
    ValidatedTextField,
  },
  setup() {
    const username = ref("");
    const password = ref("");
    const remindPassword = ref(false);
    const showConfirmation = ref(false);
    const showPassword = ref(false);
    // Snapshot the shared "session expired" flag on arrival, then reset it so it
    // shows once and doesn't leak into a later, intentional visit to login.
    const sessionWasExpired = ref(sessionExpired.value);
    sessionExpired.value = false;
    const { inProcess, authenticated, login, requestPasswordReset, clearUser } =
      useUser();
    const { addAlert, clearAllAlerts } = useAlerts();
    const { loadRecaptcha, executeRecaptcha } = useRecaptcha();

    onMounted(() => {
      loadRecaptcha();
    });
    watch(remindPassword, (val) => {
      if (!val) {
        password.value = "";
      }
      clearAllAlerts();
    });

    return {
      username,
      password,
      remindPassword,
      inProcess,
      authenticated,
      login,
      requestPasswordReset,
      clearUser,
      addAlert,
      clearAllAlerts,
      showPassword,
      showConfirmation,
      sessionWasExpired,
      loadRecaptcha,
      executeRecaptcha,
    };
  },
  beforeMount() {
    this.clearUser(this.$apollo);
  },
  methods: {
    async tryLogin() {
      this.clearAllAlerts();
      const recaptchaToken = await this.executeRecaptcha("login");
      const { logged, message } = await this.login(
        this.$apollo,
        this.username,
        this.password,
        recaptchaToken
      );
      if (!logged && message) {
        this.addAlert({
          type: "error",
          message: message,
        });
      }
    },
    async tryRemind() {
      this.clearAllAlerts();
      const [success, message] = await this.requestPasswordReset(
        this.$apollo,
        this.username
      );
      if (success) {
        // Always show the same confirmation screen — prevents username enumeration
        this.showConfirmation = true;
      } else {
        this.$nextTick(() => {
          this.addAlert({
            type: "error",
            message: message || "Something went wrong. Please try again later",
            timeout: 15,
          });
        });
      }
    },
    togglePasswordVisibility() {
      return (this.showPassword = !this.showPassword);
    },
  },
};
</script>
